import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faPlus } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { describeActions, describeMatch } from '../lib/describe';
import { unsafeRuleStops } from '../lib/ruleSafety';
import {
  ConfirmModal,
  dateTime,
  LoadError,
  Loading,
  RefreshButton
} from '../components/Common';
import RuleEditor from '../modals/RuleEditor';
import HelpTip from '../components/HelpTip';

const RulesTab = ({ data, settings, getDecision, canEdit, storesData }) => {
  const {
    rules,
    loading,
    error,
    reorderRules,
    setRuleEnabled,
    seedSystemRules,
    refresh
  } = data;
  const [ordered, setOrdered] = useState(rules);
  const [editing, setEditing] = useState(null);
  const [disableRule, setDisableRule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  useEffect(() => {
    setOrdered(rules);
  }, [rules]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = async (rule, enabled) => {
    if (!canEdit || busy) return;
    setBusy(true);
    try {
      await setRuleEnabled(rule.id, enabled);
      toast.success(`Rule ${enabled ? 'enabled' : 'disabled'}`);
      setDisableRule(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const persistOrder = async next => {
    if (!canEdit || busy) return;
    setOrdered(next);
    setBusy(true);
    try {
      await reorderRules(next.map(r => r.id));
      toast.success('Rule priorities updated');
      setPendingOrder(null);
    } catch (err) {
      setOrdered(rules);
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const onDragEnd = ({ source, destination }) => {
    if (!canEdit || busy || !destination || source.index === destination.index)
      return;
    const next = [...ordered];
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    const warnings = unsafeRuleStops(
      next.map((r, i) => ({ ...r, priority: (i + 1) * 10 }))
    );
    if (warnings.length) setPendingOrder({ rules: next, warnings });
    else persistOrder(next);
  };

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">Rules</h5>
            <p className="small text-muted mb-0">
              Rules run in priority order. Drag the handle to reorder.
            </p>
          </div>
          <div className="d-flex gap-2">
            <RefreshButton onClick={refresh} loading={loading || busy} />
            {canEdit && (
              <Button
                size="sm"
                disabled={busy || loading || !rules.length || Boolean(error)}
                onClick={() => setEditing({})}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                New rule
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {!loading && !rules.length && !error && (
            <Alert variant="warning" className="m-3">
              The backend is applying its 4 built-in safety rules. Seed them
              before adding custom rules.
              {canEdit && (
                <Button
                  size="sm"
                  className="ms-2"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await seedSystemRules();
                      toast.success('System safety rules seeded');
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Seed system rules
                </Button>
              )}
            </Alert>
          )}
          <div className="p-3 pb-0">
            <LoadError error={error} />
          </div>
          {loading ? (
            <Loading />
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle small">
                  <thead>
                    <tr>
                      <th aria-label="Drag handle" />
                      <th>Priority</th>
                      <th>Enabled</th>
                      <th>Name</th>
                      <th>IF</th>
                      <th>THEN</th>
                      <th>Stop</th>
                      <th>Type</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <Droppable droppableId="rate-shop-rules">
                    {provided => (
                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {ordered.map((rule, index) => (
                          <Draggable
                            key={rule.id}
                            draggableId={rule.id}
                            index={index}
                            isDragDisabled={!canEdit || busy}
                          >
                            {drag => (
                              <tr
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                onClick={() => setEditing(rule)}
                                style={{
                                  ...drag.draggableProps.style,
                                  cursor: 'pointer'
                                }}
                              >
                                <td onClick={e => e.stopPropagation()}>
                                  {canEdit && (
                                    <span
                                      {...drag.dragHandleProps}
                                      aria-label={`Reorder ${rule.name}`}
                                      className="px-2 text-muted"
                                    >
                                      <FontAwesomeIcon icon={faGripVertical} />
                                    </span>
                                  )}
                                </td>
                                <td>{rule.priority}</td>
                                <td onClick={e => e.stopPropagation()}>
                                  <Form.Check
                                    type="switch"
                                    aria-label={`Enable ${rule.name}`}
                                    checked={rule.enabled !== false}
                                    disabled={!canEdit || busy}
                                    onChange={e => {
                                      if (rule.system && !e.target.checked)
                                        setDisableRule(rule);
                                      else toggle(rule, e.target.checked);
                                    }}
                                  />
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 text-start"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setEditing(rule);
                                    }}
                                  >
                                    {rule.name}
                                  </Button>
                                </td>
                                <td style={{ minWidth: 220 }}>
                                  {describeMatch(rule.match, storesData.stores)}
                                </td>
                                <td style={{ minWidth: 240 }}>
                                  {describeActions(rule.actions)}
                                </td>
                                <td>
                                  {rule.stopProcessing ? (
                                    <span>
                                      <Badge bg="warning">Stop</Badge>
                                      <HelpTip label="Stop checking rules">
                                        Stop checking other rules after this one
                                        matches. Later rules will not get a
                                        chance to add their instructions.
                                      </HelpTip>
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td>
                                  {rule.system ? (
                                    <span>
                                      <Badge bg="secondary">System</Badge>
                                      <HelpTip label="System safety rule">
                                        Set up by the backend to keep hazmat,
                                        international, manual and held orders
                                        out — disable with care.
                                      </HelpTip>
                                    </span>
                                  ) : (
                                    'Custom'
                                  )}
                                </td>
                                <td
                                  className="text-break"
                                  style={{ minWidth: 170 }}
                                >
                                  {dateTime(rule.updatedAt)}
                                  <div className="text-muted">
                                    {rule.updatedBy || '—'}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {!ordered.length && (
                          <tr>
                            <td
                              colSpan={9}
                              className="text-center p-4 text-muted"
                            >
                              Seed the system safety rules to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    )}
                  </Droppable>
                </Table>
              </div>
            </DragDropContext>
          )}
        </Card.Body>
      </Card>
      {editing && (
        <RuleEditor
          key={editing.id || 'new'}
          rule={editing.id ? editing : null}
          {...data}
          storesData={storesData}
          settings={settings}
          getDecision={getDecision}
          canEdit={canEdit}
          onHide={() => setEditing(null)}
        />
      )}
      <ConfirmModal
        show={Boolean(disableRule)}
        title="Disable system rule?"
        busy={busy}
        onHide={() => setDisableRule(null)}
        onConfirm={() => toggle(disableRule, false)}
        confirmLabel="Disable rule"
      >
        <p>
          “{disableRule?.name}” is a system safety rule. Disabling it may allow
          previously protected orders to be changed.
        </p>
      </ConfirmModal>
      <ConfirmModal
        show={Boolean(pendingOrder)}
        title="Bypass system safety rules?"
        busy={busy}
        onHide={() => setPendingOrder(null)}
        onConfirm={() => persistOrder(pendingOrder.rules)}
        confirmLabel="Confirm new order"
      >
        {pendingOrder?.warnings.map(({ rule, systemRules }) => (
          <p key={rule.id}>
            “{rule.name}” would stop processing before{' '}
            {systemRules.map(r => r.name).join(', ')} without skipping the
            order. Matching orders may bypass those safety checks.
          </p>
        ))}
      </ConfirmModal>
    </>
  );
};

export default RulesTab;
